import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams, useSearchParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';

import { Button } from '@shared/ui/button';
import { Select } from '@shared/ui/select';
import type { SelectOption } from '@shared/ui/select';
import { PushBadge } from '@entities/marketing-push/ui';
import {
  emptyDocument,
  languagesWithProblems,
  platformsOf,
  pushPermissions,
  validatePushDraft,
} from '@entities/marketing-push/lib';
import {
  PushAudienceBlock,
  PushAudiencePanel,
  PushContentForm,
  PushInAppPreview,
  PushLanguageTabs,
  PushSendPanel,
  PushSystemPreview,
  PushTargetPicker,
  PushTemplateBlock,
} from '@features/push-campaign-form';
import {
  asList,
  marketingPushApi,
  pushError,
} from '../../api/services/marketingPush.service';
import { CAMPAIGN_STATUS_LABEL } from '../../types/marketingPushInterfaces';
import type {
  EngagementMode,
  PushApp,
  PushAudienceEstimate,
  PushAudienceParams,
  PushCampaign as Campaign,
  PushCampaignDraft,
  PushContent,
  PushLanguage,
  PushNotificationType,
  PushTemplate,
  PushTestDevice,
} from '../../types/marketingPushInterfaces';
import type { User } from '../../types/interfaces';

const CARD = 'bg-white rounded-xl shadow-sm p-4 mb-4';
const FIELD_LABEL = 'block text-sm font-medium text-[#2d2c2c] mb-1';
const selectControlStyles = { control: () => ({ height: '44px', minHeight: '44px' }) };

const EDITABLE_STATUSES = ['draft', 'scheduled'];

function currentTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function blankContent(language: string): PushContent {
  return {
    language,
    push_title: '',
    push_body: '',
    inapp_title: '',
    inapp_body: emptyDocument(),
    navigation_title: '',
    button_label: '',
    button_url: '',
    image_url: '',
  };
}

function draftFromCampaign(campaign: Campaign): PushCampaignDraft {
  return {
    id: campaign.id,
    notification_type: campaign.notification_type,
    targets: [...campaign.targets],
    contents: campaign.contents.map((content) => ({ ...content })),
    is_test: campaign.is_test,
    scheduled_at: campaign.scheduled_at,
    schedule_timezone: campaign.schedule_timezone || currentTimezone(),
    engagement_mode: campaign.engagement_mode,
    engagement_days: campaign.engagement_days,
    engagement_from_days: campaign.engagement_from_days,
    engagement_to_days: campaign.engagement_to_days,
  };
}

/**
 * Форма подготовки рассылки.
 *
 * Состав блоков тот же, что в старом инструменте: шаблон, тип, приложения,
 * языки, контент, аудитория, время отправки. Отличий три, и все закрывают
 * то, из-за чего к старому инструменту были вопросы: появился черновик
 * (раньше сохранение и отправка были одним действием), предпросмотр под обе
 * платформы стоит рядом с полями, а расчёт получателей с разбивкой доступен
 * до отправки.
 *
 * Состояние черновика — локальное, как в остальных формах ЛК. Redux здесь
 * не нужен: черновик живёт ровно столько, сколько открыта страница.
 */
export default function PushCampaign() {
  const params = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const { me }: { me: User } = useOutletContext();
  const permissions = pushPermissions(me);
  const [searchParams, setSearchParams] = useSearchParams();

  const campaignId = params.id && params.id !== 'new' ? Number(params.id) : undefined;

  const [apps, setApps] = useState<PushApp[]>([]);
  const [languages, setLanguages] = useState<PushLanguage[]>([]);
  const [types, setTypes] = useState<PushNotificationType[]>([]);
  const [testDevices, setTestDevices] = useState<PushTestDevice[]>([]);
  const [templates, setTemplates] = useState<PushTemplate[]>([]);

  const [campaign, setCampaign] = useState<Campaign | undefined>();
  const [draft, setDraft] = useState<PushCampaignDraft | null>(null);
  const [activeLanguage, setActiveLanguage] = useState('');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [estimate, setEstimate] = useState<PushAudienceEstimate | undefined>();
  const [estimating, setEstimating] = useState(false);

  /**
   * Ключ идемпотентности. Создаётся один раз и живёт до успешной отправки:
   * если ответ потерялся в сети, повтор должен уйти с тем же ключом, иначе
   * сервер создаст вторую рассылку. Блокировка кнопки от этого не спасает.
   */
  const [idempotencyKey, setIdempotencyKey] = useState('');

  /* -- загрузка справочников -- */
  useEffect(() => {
    Promise.all([
      marketingPushApi.getApps(),
      marketingPushApi.getLanguages(),
      marketingPushApi.getNotificationTypes(),
      marketingPushApi.getTestDevices(),
      marketingPushApi.getTemplates(),
    ])
      .then(([appsRes, langRes, typesRes, devicesRes, templatesRes]) => {
        setApps(asList(appsRes.data));
        setLanguages(asList(langRes.data));
        setTypes(asList(typesRes.data));
        setTestDevices(asList(devicesRes.data));
        setTemplates(asList(templatesRes.data));
      })
      .catch((loadError) => setError(pushError(loadError, 'Не удалось загрузить справочники')));
  }, []);

  /* -- загрузка рассылки либо новый черновик -- */
  useEffect(() => {
    if (campaignId === undefined) {
      if (types.length && languages.length && !draft) {
        setDraft({
          notification_type: types.find((type) => type.is_active)?.code ?? types[0].code,
          targets: [],
          contents: [blankContent(languages[0].code)],
          is_test: false,
          scheduled_at: null,
          schedule_timezone: currentTimezone(),
          engagement_mode: 'ignore',
          engagement_days: null,
          engagement_from_days: null,
          engagement_to_days: null,
        });
        setActiveLanguage(languages[0].code);
        setLoading(false);
      }
      return;
    }

    marketingPushApi
      .getCampaignById(campaignId)
      .then((response) => {
        setCampaign(response.data);
        setDraft(draftFromCampaign(response.data));
        setActiveLanguage(response.data.contents[0]?.language ?? '');
        setDirty(false);
      })
      .catch((loadError) => setError(pushError(loadError, 'Не удалось загрузить рассылку')))
      .finally(() => setLoading(false));
  }, [campaignId, draft, languages, types]);

  const loadEstimate = useCallback(() => {
    if (campaignId === undefined) return;
    setEstimating(true);
    marketingPushApi
      .getAudienceEstimate(campaignId)
      .then((response) => setEstimate(response.data))
      .catch(() => setEstimate(undefined))
      .finally(() => setEstimating(false));
  }, [campaignId]);

  useEffect(() => {
    loadEstimate();
  }, [loadEstimate]);

  const applyTemplate = useCallback(
    (template: PushTemplate) => {
      setDraft((current) =>
        current
          ? {
              ...current,
              notification_type: template.notification_type ?? current.notification_type,
              targets: [...template.targets],
              contents: template.contents.map((content) => ({ ...content })),
              engagement_mode: template.engagement_mode,
              engagement_days: template.engagement_days,
              engagement_from_days: template.engagement_from_days,
              engagement_to_days: template.engagement_to_days,
            }
          : current,
      );
      setActiveLanguage(template.contents[0]?.language ?? '');
      setDirty(true);
      setNotice(`Шаблон «${template.name}» применён.`);
    },
    [],
  );

  /* Переход «Использовать шаблон» приходит как ?template=<id>. Применяем
     один раз и убираем параметр из адреса, иначе шаблон переприменится при
     любой перерисовке и затрёт правки. */
  useEffect(() => {
    const requested = searchParams.get('template');
    if (!requested || !draft || !templates.length) return;

    const template = templates.find((item) => String(item.id) === requested);
    if (template) applyTemplate(template);

    searchParams.delete('template');
    setSearchParams(searchParams, { replace: true });
  }, [applyTemplate, draft, searchParams, setSearchParams, templates]);

  const allTargets = useMemo(() => apps.flatMap((app) => app.targets), [apps]);
  const platforms = useMemo(
    () => platformsOf(draft?.targets ?? [], allTargets),
    [allTargets, draft?.targets],
  );
  const problems = useMemo(() => validatePushDraft(draft), [draft]);
  const problemLanguages = useMemo(() => languagesWithProblems(draft), [draft]);
  const selectedType = types.find((type) => type.code === draft?.notification_type);
  const activeContent = draft?.contents.find((content) => content.language === activeLanguage);

  const readOnly = Boolean(campaign && !EDITABLE_STATUSES.includes(campaign.status));

  if (loading || !draft) {
    return <div className="p-6 text-sm text-gray-500">Загрузка…</div>;
  }

  const previewAppName =
    allTargets.find((target) => draft.targets.includes(target.code))?.display_name ??
    'Приложение';

  function patchContent(patch: Partial<PushContent>) {
    setDraft((current) =>
      current
        ? {
            ...current,
            contents: current.contents.map((content) =>
              content.language === activeLanguage ? { ...content, ...patch } : content,
            ),
          }
        : current,
    );
    setDirty(true);
  }

  function patchAudience(patch: Partial<PushAudienceParams>) {
    setDraft((current) => (current ? { ...current, ...patch } : current));
    setDirty(true);
  }

  function changeMode(mode: EngagementMode) {
    setDraft((current) =>
      current
        ? {
            ...current,
            engagement_mode: mode,
            // Сбрасываем параметры прежнего режима: остатки от «в диапазоне»
            // при переключении на «дольше N дней» дали бы непредсказуемую
            // выборку.
            engagement_days: mode === 'ignore' ? null : current.engagement_days,
            engagement_from_days: mode === 'between' ? current.engagement_from_days : null,
            engagement_to_days: mode === 'between' ? current.engagement_to_days : null,
          }
        : current,
    );
    setDirty(true);
  }

  function toggleTarget(code: string) {
    setDraft((current) =>
      current
        ? {
            ...current,
            targets: current.targets.includes(code)
              ? current.targets.filter((item) => item !== code)
              : [...current.targets, code],
          }
        : current,
    );
    setDirty(true);
  }

  function addLanguage(code: string) {
    setDraft((current) => {
      if (!current || current.contents.some((content) => content.language === code)) {
        return current;
      }
      return { ...current, contents: [...current.contents, blankContent(code)] };
    });
    setActiveLanguage(code);
    setDirty(true);
  }

  function removeLanguage(code: string) {
    setDraft((current) => {
      if (!current) return current;
      const contents = current.contents.filter((content) => content.language !== code);
      if (activeLanguage === code) setActiveLanguage(contents[0]?.language ?? '');
      return { ...current, contents };
    });
    setDirty(true);
  }

  async function handleSave() {
    setError('');
    setSaving(true);
    try {
      if (campaignId === undefined) {
        const { data } = await marketingPushApi.createCampaign(draft!);
        setDirty(false);
        navigate(`/marketing/push/${data.id}`, { replace: true });
        return;
      }
      const { data } = await marketingPushApi.updateCampaign(campaignId, draft!);
      setCampaign(data);
      setDraft(draftFromCampaign(data));
      setDirty(false);
      setNotice('Черновик сохранён.');
      loadEstimate();
    } catch (saveError) {
      const message = pushError(saveError, 'Не удалось сохранить черновик');
      setError(message);
      throw new Error(message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSend() {
    if (campaignId === undefined) throw new Error('Сначала сохраните черновик.');
    const key = idempotencyKey || generateKey();
    if (!idempotencyKey) setIdempotencyKey(key);

    try {
      await marketingPushApi.sendCampaign(campaignId, key);
      setIdempotencyKey('');
      navigate(`/marketing/push/${campaignId}/report`);
    } catch (sendError) {
      throw new Error(pushError(sendError, 'Не удалось отправить рассылку'));
    }
  }

  const typeOptions: SelectOption[] = types
    .filter((type) => type.is_active)
    .map((type) => ({ value: type.code, label: type.name }));

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/marketing/push')}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <FiArrowLeft /> К рассылкам
        </button>
        <h1 className="text-xl font-semibold text-[#2d2c2c]">
          {campaignId === undefined ? 'Новая рассылка' : `Рассылка #${campaignId}`}
        </h1>
        {campaign && (
          <PushBadge tone={CAMPAIGN_STATUS_LABEL[campaign.status].tone}>
            {CAMPAIGN_STATUS_LABEL[campaign.status].label}
          </PushBadge>
        )}
        {campaign?.is_test && <PushBadge tone="info">тест</PushBadge>}
        {campaign && ['queued', 'running', 'handed_off', 'delivered', 'partially_delivered', 'failed'].includes(campaign.status) && (
          <button
            type="button"
            onClick={() => navigate(`/marketing/push/${campaignId}/report`)}
            className="ml-auto text-sm text-blue-600 hover:underline"
          >
            Отчёт
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}
      {notice && (
        <div className="mb-4 rounded-lg bg-green-50 p-3 text-sm text-green-700">{notice}</div>
      )}

      {readOnly && (
        <div className="mb-4 rounded-lg bg-sky-50 p-3 text-sm text-sky-800">
          <strong className="block">Только просмотр</strong>
          Рассылку в статусе «{campaign && CAMPAIGN_STATUS_LABEL[campaign.status].label}»
          изменить нельзя. Чтобы отправить похожую, скопируйте её из истории.
        </div>
      )}

      {campaign?.error_message && (
        <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          <strong className="block">Ошибка отправки</strong>
          {campaign.error_message}
        </div>
      )}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0">
          {!readOnly && (
            <PushTemplateBlock
              draft={draft}
              canManage={permissions.manageTemplates}
              onApply={applyTemplate}
              onSaved={setNotice}
            />
          )}

          <div className={CARD}>
            <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">Основное</h2>
            <div className="w-full max-w-[420px]">
              <label className={FIELD_LABEL}>
                Тип уведомления <span className="text-red-500">*</span>
              </label>
              <Select
                options={typeOptions}
                optionSelected={
                  typeOptions.find((option) => option.value === draft.notification_type) ?? null
                }
                isVisible={false}
                isClearable={false}
                customStyles={selectControlStyles}
                onSelected={(option: SelectOption | null) => {
                  if (!option) return;
                  setDraft({ ...draft, notification_type: String(option.value) });
                  setDirty(true);
                }}
              />
            </div>

            {selectedType?.requires_consent && (
              <div className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                <strong className="block">Тип требует согласия</strong>
                Уведомление получат только те, кто дал согласие на рекламные рассылки
                в приложении. Согласие нельзя получить задним числом, поэтому аудитория
                будет заметно меньше обычной.
              </div>
            )}
          </div>

          <PushTargetPicker
            apps={apps}
            selected={draft.targets}
            disabled={readOnly}
            onToggle={toggleTarget}
          />

          <PushLanguageTabs
            languages={languages}
            selected={draft.contents.map((content) => content.language)}
            active={activeLanguage}
            problems={problemLanguages}
            disabled={readOnly}
            onSelect={setActiveLanguage}
            onAdd={addLanguage}
            onRemove={removeLanguage}
          />

          {activeContent ? (
            <PushContentForm
              content={activeContent}
              platforms={platforms}
              disabled={readOnly}
              onPatch={patchContent}
            />
          ) : (
            <div className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              Добавьте язык, чтобы заполнить контент.
            </div>
          )}

          <PushAudienceBlock
            audience={draft}
            disabled={readOnly}
            onChange={patchAudience}
            onModeChange={changeMode}
          />
        </div>

        <div className="min-w-0">
          <div className={CARD}>
            <h2 className="mb-3 text-base font-semibold text-[#2d2c2c]">Предпросмотр</h2>

            {platforms.length === 0 ? (
              <p className="text-sm text-gray-500">
                Выберите приложение, чтобы увидеть, как уведомление выглядит на его
                платформе.
              </p>
            ) : (
              <div className="grid gap-4">
                {platforms.map((platform) => (
                  <PushSystemPreview
                    key={platform}
                    platform={platform}
                    appName={previewAppName}
                    title={activeContent?.push_title ?? ''}
                    body={activeContent?.push_body ?? ''}
                    softLimits={activeContent?.soft_limits}
                  />
                ))}
              </div>
            )}

            {activeContent && (
              <div className="mt-4">
                <p className="mb-2 text-xs uppercase tracking-wide text-gray-500">
                  Внутри приложения
                </p>
                <PushInAppPreview
                  navigationTitle={activeContent.navigation_title}
                  title={activeContent.inapp_title}
                  body={activeContent.inapp_body}
                  imageUrl={activeContent.image_url}
                  buttonLabel={activeContent.button_label}
                />
              </div>
            )}
          </div>

          <PushAudiencePanel
            estimate={estimate}
            loading={estimating}
            notificationType={selectedType}
            languages={languages}
            saveFirst={campaignId === undefined}
            onRefresh={loadEstimate}
          />

          <PushSendPanel
            campaign={campaign}
            estimate={estimate}
            problems={problems}
            dirty={dirty}
            canSend={permissions.send && !readOnly}
            saving={saving}
            testDevices={testDevices}
            timezone={draft.schedule_timezone}
            onSave={handleSave}
            onSend={handleSend}
            onSchedule={async (isoDateTime, timezone) => {
              if (campaignId === undefined) throw new Error('Сначала сохраните черновик.');
              try {
                const { data } = await marketingPushApi.scheduleCampaign(
                  campaignId,
                  isoDateTime,
                  timezone,
                );
                setCampaign((current) =>
                  current ? { ...current, status: data.status as Campaign['status'], scheduled_at: data.scheduled_at } : current,
                );
              } catch (scheduleError) {
                throw new Error(pushError(scheduleError, 'Не удалось запланировать'));
              }
            }}
            onCancel={async () => {
              if (campaignId === undefined) return;
              try {
                const { data } = await marketingPushApi.cancelCampaign(campaignId);
                setCampaign((current) =>
                  current ? { ...current, status: data.status as Campaign['status'] } : current,
                );
              } catch (cancelError) {
                throw new Error(pushError(cancelError, 'Не удалось отменить'));
              }
            }}
            onTest={async (deviceIds) => {
              if (campaignId === undefined) throw new Error('Сначала сохраните черновик.');
              try {
                const { data } = await marketingPushApi.testCampaign(campaignId, deviceIds);
                return data.recipients;
              } catch (testError) {
                throw new Error(pushError(testError, 'Не удалось отправить тест'));
              }
            }}
          />

          {draft.contents.length > 1 && !readOnly && (
            <div className={CARD}>
            <Button
              isWhite
              onClick={() => {
                const source = draft.contents.find(
                  (content) => content.language === activeLanguage,
                );
                if (!source) return;
                setDraft({
                  ...draft,
                  contents: draft.contents.map((content) =>
                    content.language === source.language
                      ? content
                      : {
                          ...content,
                          image_url: source.image_url,
                          button_url: source.button_url,
                        },
                  ),
                });
                setDirty(true);
                setNotice('Картинка и ссылка скопированы в остальные языки.');
              }}
            >
              Скопировать картинку и ссылку в остальные языки
            </Button>
            <p className="mt-2 text-xs text-gray-500">
              Скопируются картинка и ссылка кнопки. Тексты не копируются намеренно:
              перенести русский текст в английскую версию и отправить так — типовая
              ошибка, которую легко не заметить.
            </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function generateKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
